/**
 * MIA Polling Service
 * Handles status polling for MIA AI operations
 * Matches Flutter's polling behavior (3 second interval, 3 minute timeout)
 */

import { PollingConfig } from "../types";
import { apiGet } from "./api";

/**
 * Polling task class
 */
class PollingTask {
  constructor({
    id,
    endpoint,
    onUpdate,
    onComplete,
    onFailed,
    onTimeout,
    checkComplete,
    checkFailed,
  }) {
    this.id = id;
    this.endpoint = endpoint;
    this.onUpdate = onUpdate;
    this.onComplete = onComplete;
    this.onFailed = onFailed;
    this.onTimeout = onTimeout;
    this.checkComplete =
      checkComplete || ((data) => data?.status === "completed");
    this.checkFailed = checkFailed || ((data) => data?.status === "failed");
    this.intervalId = null;
    this.startTime = null;
    this.isActive = false;
  }
}

/**
 * Active polling tasks
 */
const activeTasks = new Map();

/**
 * Start polling for a specific endpoint
 * @param {Object} options - Polling options
 * @param {string} options.id - Unique identifier for this polling task
 * @param {string} options.endpoint - API endpoint to poll
 * @param {Function} options.onUpdate - Callback when status is updated
 * @param {Function} options.onComplete - Callback when operation completes
 * @param {Function} options.onFailed - Callback when operation fails
 * @param {Function} options.onTimeout - Callback when polling times out
 * @param {Function} options.checkComplete - Function to check if operation is complete
 * @param {Function} options.checkFailed - Function to check if operation failed
 * @returns {Function} Stop polling function
 */
export function startPolling({
  id,
  endpoint,
  onUpdate,
  onComplete,
  onFailed,
  onTimeout,
  checkComplete,
  checkFailed,
}) {
  // Stop any existing polling for this ID
  stopPolling(id);

  const task = new PollingTask({
    id,
    endpoint,
    onUpdate,
    onComplete,
    onFailed,
    onTimeout,
    checkComplete,
    checkFailed,
  });

  task.startTime = Date.now();
  task.isActive = true;

  // Perform initial check
  performPoll(task);

  // Start interval polling
  task.intervalId = setInterval(() => {
    performPoll(task);
  }, PollingConfig.INTERVAL_MS);

  activeTasks.set(id, task);

  // Return stop function
  return () => stopPolling(id);
}

/**
 * Perform a single poll
 * @param {PollingTask} task
 */
async function performPoll(task) {
  if (!task.isActive) return;

  // Check for timeout (3 minutes)
  const elapsed = Date.now() - task.startTime;
  if (elapsed >= PollingConfig.TIMEOUT_MS) {
    console.log(`[PollingService] Timeout for task: ${task.id}`);
    stopPolling(task.id);
    if (task.onTimeout) {
      task.onTimeout();
    }
    return;
  }

  try {
    const result = await apiGet(task.endpoint);

    if (!task.isActive) return;

    if (result.error) {
      console.error(
        `[PollingService] Error polling ${task.endpoint}:`,
        result.error
      );
      // Continue polling on error
      return;
    }

    const data = result.data;

    // Notify update
    if (task.onUpdate) {
      task.onUpdate(data);
    }

    // Check if completed
    if (task.checkComplete(data)) {
      console.log(`[PollingService] Task completed: ${task.id}`);
      stopPolling(task.id);
      if (task.onComplete) {
        task.onComplete(data);
      }
      return;
    }

    // Check if failed
    if (task.checkFailed(data)) {
      console.log(`[PollingService] Task failed: ${task.id}`);
      stopPolling(task.id);
      if (task.onFailed) {
        task.onFailed(data);
      }
      return;
    }
  } catch (e) {
    console.error(`[PollingService] Exception polling ${task.endpoint}:`, e);
    // Continue polling on error
  }
}

/**
 * Stop polling for a specific task
 * @param {string} id - Task ID
 */
export function stopPolling(id) {
  const task = activeTasks.get(id);
  if (task) {
    task.isActive = false;
    if (task.intervalId) {
      clearInterval(task.intervalId);
      task.intervalId = null;
    }
    activeTasks.delete(id);
    console.log(`[PollingService] Stopped polling for: ${id}`);
  }
}

/**
 * Stop all active polling tasks
 */
export function stopAllPolling() {
  activeTasks.forEach((task, id) => {
    stopPolling(id);
  });
}

/**
 * Check if a polling task is active
 * @param {string} id - Task ID
 * @returns {boolean}
 */
export function isPollingActive(id) {
  const task = activeTasks.get(id);
  return task?.isActive || false;
}

/**
 * Get elapsed time for a polling task
 * @param {string} id - Task ID
 * @returns {number|null} Elapsed time in milliseconds
 */
export function getPollingElapsedTime(id) {
  const task = activeTasks.get(id);
  if (!task || !task.startTime) return null;
  return Date.now() - task.startTime;
}

/**
 * Restart polling for a task (useful after retry)
 * @param {string} id - Task ID
 */
export function restartPolling(id) {
  const task = activeTasks.get(id);
  if (task) {
    task.startTime = Date.now();
    task.isActive = true;

    // Clear existing interval
    if (task.intervalId) {
      clearInterval(task.intervalId);
    }

    // Perform initial check
    performPoll(task);

    // Start new interval
    task.intervalId = setInterval(() => {
      performPoll(task);
    }, PollingConfig.INTERVAL_MS);
  }
}

/**
 * Create a polling composable for Vue components
 * @returns {Object} Polling composable
 */
export function usePolling() {
  const activePolls = new Set();

  const start = (options) => {
    const stop = startPolling(options);
    activePolls.add(options.id);
    return stop;
  };

  const stop = (id) => {
    stopPolling(id);
    activePolls.delete(id);
  };

  const stopAll = () => {
    activePolls.forEach((id) => {
      stopPolling(id);
    });
    activePolls.clear();
  };

  // Cleanup function for component unmount
  const cleanup = () => {
    stopAll();
  };

  return {
    start,
    stop,
    stopAll,
    cleanup,
    isActive: isPollingActive,
    getElapsedTime: getPollingElapsedTime,
    restart: restartPolling,
  };
}
